import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
    detectSubscriptionAccountMismatch,
    SubscriptionAccountMismatch,
} from "../../services/subscriptionAccountMismatch";

interface UseSubscriptionAccountMismatchResult {
    accountMismatch: SubscriptionAccountMismatch | null;
    dismiss: () => void;
}

// REFSIX-41 once-per-session detection hook. Lives at Root so the alert
// renders regardless of which tab the user is on when detection completes.
//
// Gates the detection on:
//   - `isAuthenticated` (no point before login)
//   - `subscriptions.updatedAt > sessionStartTs` — the subscription state
//      has been refreshed THIS session. `updatedAt` is persisted across
//      sessions so a stale rehydrated value would otherwise trip the
//      detection against out-of-date subscription data; comparing to a
//      mount-time `sessionStartTs` filters those out.
//   - `hasCheckedMismatch.current === false` (once-per-session)
//
// The detector itself short-circuits when the signed-in user already
// has a paid sub, so this only ever surfaces an alert for the cohort
// the ticket targets: free / expired users whose receipt on device is
// bound to a different REFSIX account.
//
// The reset effect clears the guard + alert state when `isAuthenticated`
// drops, so a logout → re-login as a different user gets its own fresh
// detection.
export const useSubscriptionAccountMismatch = (
    isAuthenticated: boolean
): UseSubscriptionAccountMismatchResult => {
    // useMemo with an empty dep array captures Date.now() once on mount
    // and reuses it across every subsequent render. The previous
    // `useRef(Date.now()).current` pattern got the same stable value
    // (useRef ignores its argument after the first call) but
    // re-invoked Date.now() on every render only to discard the
    // result. Cleaner, no behaviour change.
    const sessionStartTs = useMemo(() => Date.now(), []);
    const hasCheckedMismatch = useRef(false);
    const [accountMismatch, setAccountMismatch] =
        useState<SubscriptionAccountMismatch | null>(null);
    const subscriptionsUpdatedAt = useSelector(
        (state: RootState) => state.subscriptions.updatedAt
    );

    useEffect(() => {
        // `cancelled` guards the async write: a logout (or unmount, or
        // re-login as a different user) during the in-flight
        // getStoreReceipt() + lookupUsernameByReceipt() must not let the
        // late `.then` resurface an alert for a session that no longer
        // applies. The detector's internal post-await user_id re-check
        // covers the pure-logout case; this also covers unmount and the
        // logout→re-login race, and avoids a state-update-after-unmount.
        let cancelled = false;
        if (
            isAuthenticated &&
            subscriptionsUpdatedAt &&
            subscriptionsUpdatedAt > sessionStartTs &&
            !hasCheckedMismatch.current
        ) {
            hasCheckedMismatch.current = true;
            detectSubscriptionAccountMismatch().then((result) => {
                if (!cancelled) setAccountMismatch(result);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, subscriptionsUpdatedAt, sessionStartTs]);

    useEffect(() => {
        if (!isAuthenticated) {
            hasCheckedMismatch.current = false;
            setAccountMismatch(null);
        }
    }, [isAuthenticated]);

    return {
        accountMismatch,
        dismiss: () => setAccountMismatch(null),
    };
};
